import React, { useState, useEffect } from 'react';
import { StyleSheet, css } from 'aphrodite';
import { motion } from 'framer-motion';
import {
  IconButton,
  Badge,
  Avatar,
  Menu,
  MenuItem,
  Divider,
  ListItemIcon
} from '@mui/material';
import {
  Menu as MenuIcon,
  Notifications,
  ArrowBack,
  Settings,
  Logout,
  Person
} from '@mui/icons-material';
import { useLocation, useNavigate } from 'react-router-dom';
import { APP_URLS } from '../../../../urls';
import { handleLogout } from '../../../utils/user/patient/authHelpers';
import { toast } from 'react-toastify';
import { useDispatch } from 'react-redux';

const AppBar = ({
  isMobile,
  isSidebarVisible,
  toggleSidebarVisibility,
  notificationCount = 0
}) => {
  const dispatch = useDispatch();
  const location = useLocation();
  const navigate = useNavigate();
  const [title, setTitle] = useState('Dashboard');
  const [showBackButton, setShowBackButton] = useState(false);
  const [anchorEl, setAnchorEl] = useState(null);
  const isMenuOpen = Boolean(anchorEl);

  // Update title based on current path
  useEffect(() => {
    const path = location.pathname;
    let pageTitle = 'Dashboard';
    let showBack = false;

    if (path.includes('dashboard')) {
      pageTitle = 'Dashboard';
    } else if (path.includes('purchases')) {
      pageTitle = 'Medical Purchases';
    } else if (path.includes('treatments')) {
      pageTitle = 'Treatment History';
    } else if (path.includes('share')) {
      pageTitle = 'Share Medical Data';
    } else if (path.includes('settings')) {
      pageTitle = 'Settings';
    } else if (path.includes('notifications')) {
      pageTitle = 'Notifications';
    }

    // Determine if back button should show
    if (path.includes('/details/') || path.includes('/edit/')) {
      showBack = true;
    }

    setTitle(pageTitle);
    setShowBackButton(showBack);
  }, [location]);

  const handleProfileMenu = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleCloseMenu = () => {
    setAnchorEl(null);
  };

  const handleBackButton = () => {
    navigate(-1);
  };

  const handleNavigation = (path) => {
    navigate(path);
    handleCloseMenu();
  };

  return (
    <motion.div
      className={css(styles.appBar)}
      initial={{ y: -50 }}
      animate={{ y: 0 }}
      transition={{ type: 'spring', stiffness: 300, damping: 30 }}
    >
      <div className={css(styles.leftSection)}>
        {showBackButton ? (
          <IconButton
            color='inherit'
            onClick={handleBackButton}
            className={css(styles.iconButton)}
          >
            <ArrowBack />
          </IconButton>
        ) : (
        // Only show menu button on mobile
				  isMobile && (
  <IconButton
    color='inherit'
    onClick={toggleSidebarVisibility}
    className={css(styles.iconButton)}
  >
    <MenuIcon />
  </IconButton>
				  )
        )}
        {!isSidebarVisible
          ? (
            <h1 className={css(styles.title)}>{title}</h1>
            )
          : null}
      </div>

      <div className={css(styles.rightSection)}>
        <IconButton
          color='inherit'
          onClick={() => navigate(APP_URLS.patientNotifications)}
          className={css(styles.iconButton)}
        >
          <Badge badgeContent={notificationCount} color='error'>
            <Notifications />
          </Badge>
        </IconButton>

        <IconButton
          onClick={handleProfileMenu}
          className={css(styles.profileButton)}
        >
          <Avatar className={css(styles.avatar)}>
            <Person />
          </Avatar>
        </IconButton>

        <Menu
          anchorEl={anchorEl}
          open={isMenuOpen}
          onClose={handleCloseMenu}
          PaperProps={{
					  elevation: 3,
					  sx: {
					    overflow: 'visible',
					    filter: 'drop-shadow(0px 2px 8px rgba(0,0,0,0.1))'
            }
          }}
          transformOrigin={{ horizontal: 'right', vertical: 'top' }}
          anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
        >
          <MenuItem onClick={() => handleNavigation(APP_URLS.patientProfile)}>
            <ListItemIcon>
              <Person fontSize='small' />
            </ListItemIcon>
            Profile
          </MenuItem>
          <MenuItem onClick={() => handleNavigation(APP_URLS.patientSettings)}>
            <ListItemIcon>
              <Settings fontSize='small' />
            </ListItemIcon>
            Settings
          </MenuItem>
          <Divider />
          <MenuItem onClick={() => handleLogout(toast, dispatch, navigate)}>
            <ListItemIcon>
              <Logout fontSize='small' />
            </ListItemIcon>
            Logout
          </MenuItem>
        </Menu>
      </div>
    </motion.div>
  );
};

const styles = StyleSheet.create({
  appBar: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '0.5rem 1.15rem',
    backgroundColor: 'white',
    boxShadow: '0 2px 4px rgba(0, 0, 0, 0.05)',
    height: '64px',
    position: 'sticky',
    top: 0,
    zIndex: 995,
    overflow: 'visible'
  },
  leftSection: {
    display: 'flex',
    alignItems: 'center'
  },
  rightSection: {
    display: 'flex',
    alignItems: 'center'
  },
  title: {
    margin: 0,
    fontSize: '1.25rem',
    fontWeight: 'bold',
    color: '#2783F7'
  },
  iconButton: {
    marginRight: '0.5rem',
    color: '#333'
  },
  profileButton: {
    marginLeft: '0.5rem'
  },
  avatar: {
    backgroundColor: '#e6f0fd',
    color: '#2783F7'
  }
});

export default AppBar;
